import { useEffect, useRef } from 'react';
import { BOARD_SIZE, type GameEvent } from '@tigrinho/engine';
import { type VoiceLine, audio } from '../lib/audio';
import { useRoomCtx } from '../screens/RoomScreen';
import { useCountdown } from './Hud';
import { decider, decision } from './selectors';

const VOICE_PRELOAD: VoiceLine[] = ['facada', 'mortal', 'quebrou', 'cadeia', 'hotel', 'grupo', 'derrubou'];

/** Liga eventos da partida aos sons: efeitos, locutor e trilha que acompanha a tensão. */
export function useGameAudio(): void {
  const { bus, room, you } = useRoomCtx();
  const g = room.game;
  const tension = g?.tension ?? 1;
  const finished = room.status === 'finished';

  // Trilha: começa com a partida e sobe de camada a cada nível de tensão.
  useEffect(() => {
    if (finished) {
      audio.stopMusic();
      return;
    }
    const start = () => {
      if (!audio.ready) return false;
      // Sem música por enquanto (o Matheus ainda não achou uma trilha que combine).
      audio.preloadVoices(VOICE_PRELOAD);
      return true;
    };
    if (start()) return;
    // O som só liga depois do primeiro toque na tela.
    const retry = () => {
      if (start()) window.removeEventListener('pointerdown', retry, true);
    };
    window.addEventListener('pointerdown', retry, true);
    return () => window.removeEventListener('pointerdown', retry, true);
  }, [tension, finished]);

  useEffect(() => () => audio.stopMusic(), []);

  // Efeitos e locutor a partir dos eventos.
  useEffect(() => {
    const offTaunt = bus.onTaunt((m) => {
      audio.sfx('throw');
      audio.sfx('splat', 0.62);
      if (m.combo === 5) audio.sfx('boom', 0.7);
    });
    // Os dados rolam na tela antes do resultado: o som acompanha a animação.
    const offRoll = bus.onRoll(() => {
      audio.sfx('dice');
      audio.sfx('dice', 0.55);
      audio.sfx('tap', 1.0);
    });
    const offEvents = bus.onEvents(({ events }) => {
      let walked = 0;
      for (const e of events as GameEvent[]) {
        switch (e.type) {
          case 'gameStarted':
            audio.voice('inicio', 0.4);
            break;
          case 'moved':
            if (e.how === 'walk' || e.how === 'back') {
              const n = Math.min(BOARD_SIZE, (e.how === 'walk' ? e.to - e.from + BOARD_SIZE : e.from - e.to + BOARD_SIZE) % BOARD_SIZE);
              for (let k = 0; k < n; k++) audio.sfx('step', walked + k * 0.17);
              walked += n * 0.17;
            } else {
              audio.sfx('whoosh');
            }
            break;
          case 'traveled':
            audio.sfx('whoosh');
            break;
          case 'passedStart':
            audio.sfx('coin', walked);
            break;
          case 'takeover':
            audio.sfx('facada', walked);
            audio.sfx('cash', walked + 0.3);
            break;
          case 'forcedPurchase':
            audio.sfx('cash', walked);
            break;
          case 'rentPaid':
            if (e.mortal) {
              audio.sfx('mortal', walked);
              audio.voice('mortal', walked + 0.5);
            } else if (e.facada) {
              audio.sfx('facada', walked);
              audio.voice('facada', walked + 0.5);
            } else audio.sfx('coinDown', walked);
            break;
          case 'paid':
            if (e.reason !== 'aluguel') audio.sfx('coinDown', walked);
            break;
          case 'received':
            audio.sfx('coin', walked);
            break;
          case 'cardDrawn':
            audio.sfx('card', walked);
            break;
          case 'built':
            audio.sfx(e.from < 0 ? 'cash' : 'build', walked);
            if (e.from < 0 && e.level > 0) audio.sfx('build', walked + 0.25);
            break;
          case 'hotelBuilt':
            audio.sfx('hotel');
            if (e.firstOfGame) audio.voice('hotel', 0.6);
            break;
          case 'groupCompleted':
            audio.voice('grupo', walked + 0.4);
            break;
          case 'bankrupt':
            audio.sfx('bankrupt', walked + 0.3);
            audio.voice('quebrou', walked + 1.9);
            break;
          case 'sentToJail':
            audio.sfx('jail', walked);
            audio.voice('cadeia', walked + 0.6);
            break;
          case 'houseDestroyed':
            audio.sfx('boom');
            audio.voice('derrubou', 0.5);
            break;
          case 'shieldBlocked':
            audio.sfx('shield');
            break;
          case 'tensionChanged':
            audio.sfx('tension');
            if (e.level >= 2) audio.voice(`tensao${e.level}` as VoiceLine, 1.6);
            break;
          case 'caosAdded':
            audio.voice('caos', 2.4);
            break;
          case 'finalRoundStarted':
            audio.voice('ultimaRodada', 0.3);
            break;
          case 'turnStarted':
            if (e.player === you) audio.sfx('turn', 0.2);
            break;
          case 'gameEnded':
            audio.sfx('win', 0.3);
            audio.voice('vitoria', 2.2);
            break;
          default:
            break;
        }
      }
    });
    return () => {
      offRoll();
      offTaunt();
      offEvents();
    };
  }, [bus, you]);

  // Tique-taque nos 5 últimos segundos de uma decisão minha.
  const cd = useCountdown();
  const mine = g ? decider(decision(g)) === you : false;
  const sec = cd ? Math.ceil(cd.left / 1000) : 0;
  const lastTick = useRef(0);
  useEffect(() => {
    if (!mine || sec > 5 || sec < 1 || lastTick.current === sec) return;
    lastTick.current = sec;
    audio.sfx('tick');
  }, [mine, sec]);
}
